import { notification, Modal, Spin, Dropdown, Space, Input, Button } from "antd";
import { DownOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../component/auth/AuthContext';
import {
  getUserList,
  removeUser,
  updateUserPassword,
  updateUser,
  getGroupList,
  createGroup,
  removeGroup,
} from "../../store/user/action";
import TableUser from "./component/TableUser";
import { apiErrorText } from "../../utils/apiError";
import "./UserStyled.scss";

const User = () => {
  const { t } = useTranslation();

  const { getUser } = useAuth();
  const [tableParams, setTableParams] = useState({
    pageNumber: 1,
    pageSize: 30,
    username: getUser()?.data?.username || "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [groupList, setGroupList] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showAddGroupInput, setShowAddGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const addGroupInputRef = useRef(null);
  
  const updateTimerRef = useRef(null);

  const onChangePage = (pagination, _, sorter) => {
    
    let sortParams = {};
    if (sorter.order) {
      sortParams = {
        sortBy: sorter.order === undefined ? "" : sorter.field,
        type: sorter.order === undefined ? "" : sorter.order,
      };
    }

    const newParams = {
      ...tableParams,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      ...sortParams
    };
    
    getTableUserList({
      ...newParams,
      pageNumber: pagination.current
    });
    setTableParams(newParams);
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const getTableUserList = async (payload) => {
    try {
      setLoading(true);

      const response = await getUserList(payload);
      
      if (response) {
        setData(response?.content);
        setTotalElements(response?.totalElements);
      }
    } catch (error) {
      console.error('데이터 로딩 중 오류:', error);
      setData([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const showConfirm = (dataTable) => {
    Modal.confirm({
      title: t('users.del.title'),
      content: (
        <div style={{
          fontSize: '14px',
          lineHeight: '1.8',
          marginTop: '8px'
        }}>
          {t('users.del.line1')}
          <br />
          {t('users.del.line2')}
        </div>
      ),
      okText: t('users.ok'),
      cancelText: t('users.cancel'),
      centered: true,
      width: 400,
      className: 'custom-modal',
      okButtonProps: {
        style: {
          background: '#1890ff',
          borderColor: '#1890ff'
        }
      },
      cancelButtonProps: {
        style: {
          marginRight: '8px'
        }
      },
      onOk() {
        handleDeleteItem(dataTable);
      },
      onCancel() {
        console.log("cancelled");
      },
    });
  };

  const handleDeleteItem = async (dataTable) => {
    const userData = Array.isArray(dataTable) ? dataTable[0] : dataTable;

    if (!userData || !userData.id) {
      notification.error({
        message: t('users.del.failTitle'),
        description: t('users.del.invalidDesc'),
        placement: 'top',
        duration: 3,
      });
      return;
    }

    try {
      setLoading(true);
      
      const response = await removeUser(userData.id);
      if (response && response.success) {
        notification.success({
          message: t('users.del.doneTitle'),
          description: t('users.del.doneDesc'),
          placement: 'top',
          duration: 3,
        });
        getTableUserList(tableParams);
        setSelectedRowKeys([]);
      } else {
        notification.error({
          message: t('users.del.failTitle'),
          description: apiErrorText(response, 'users.del.failDesc'),
          placement: 'top',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: t('users.del.failTitle'),
        description: t('users.del.failDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (id, newPassword) => {
    try {
      const response = await updateUserPassword({
        id: id,
        password: newPassword
      });
      
      if (response) {
        notification.success({
          message: t('users.update.doneTitle'),
          description: t('users.update.passwordDone'),
          placement: 'top',
          duration: 3,
        });


      }
      getTableUserList(tableParams);
    } catch (error) {
      notification.error({
        message: t('users.update.failTitle'),
        description: t('users.update.passwordFail'),
        placement: 'top',
        duration: 3,
      });
    }
  };

  const handleUpdateUser = async (payload) => {
    try {
      const response = await updateUser(payload);
      if (response) {
        notification.success({
          message: t('users.update.doneTitle'),
          description: t('users.update.companyDone'),
          placement: 'top',
          duration: 3,
        });
        getTableUserList(tableParams);
      }
    } catch (error) {
      notification.error({
        message: t('users.update.failTitle'),
        description: t('users.update.companyFail'),
        placement: 'top',
        duration: 3,

      });
    }
  };

  const fetchGroupList = async () => {
    try {
      const response = await getGroupList();
      if (response) {
        if (Array.isArray(response)) {
          setGroupList(response);
        } else if (response.content && Array.isArray(response.content)) {
          setGroupList(response.content);
        } else {
          console.warn('예상치 못한 그룹 리스트 응답 형식:', response);
          setGroupList([]);
        }
      }
    } catch (error) {
      console.error('그룹 목록 로딩 중 오류:', error);
      setGroupList([]);
    }
  };

  const handleUpdateGroup = async (payload) => {
    // console.log("handleUpdateGroup payload:", JSON.stringify(payload, null, 2));
    try {
      const response = await updateUser(payload);
      
      if (response) {
        notification.success({
          message: t('users.update.doneTitle'),
          description: t('users.update.groupDone'),
          placement: 'top',
          duration: 3,
        });
        
        // 현재 상태 업데이트
        setData(prevData => 
          prevData.map(item => 
            item.id === payload.id 
              ? { 
                  ...item, 
                  groupId: payload.groupId,
                  groupName: payload.groupId === null 
                    ? null 
                    : groupList.find(g => g.id === payload.groupId)?.name || 
                      groupList.find(g => g.id === payload.groupId)?.groupName
                } 
              : item
          )
        );
      }
    } catch (error) {
      console.error("그룹 업데이트 오류:", error);
      notification.error({
        message: t('users.update.failTitle'),
        description: t('users.update.groupFail'),
        placement: 'top',
        duration: 3,
      });
    }
  };

  // 디바운싱을 적용한 그룹 변경 핸들러
  const handleGroupChange = (groupId, user) => {
    const payload = {
      id: user.id,
      groupId: groupId
    };
    
    // console.log("handleGroupChange payload:", JSON.stringify(payload, null, 2));
    
    // 이전 타이머가 있으면 취소
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }
    
    // 새 타이머 설정 (300ms 지연)
    updateTimerRef.current = setTimeout(() => {
      handleUpdateGroup(payload);
      updateTimerRef.current = null;
    }, 300);
  };

  // 그룹 선택 핸들러
  const handleGroupSelect = ({ key }) => {
    const groupId = key === 'all' ? null : parseInt(key);
    setSelectedGroup(groupId);
    
    // 그룹 필터링 적용하여 테이블 데이터 다시 로드
    const newParams = {
      ...tableParams,
      pageNumber: 1,
      groupId: groupId === null ? undefined : groupId
    };
    
    getTableUserList(newParams);
    setTableParams(newParams);
  };

  // 그룹 추가 핸들러
  const handleAddGroup = (e) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    setShowAddGroupInput(true);
    
    // 입력 필드가 표시된 후 포커스 설정
    setTimeout(() => {
      if (addGroupInputRef.current) {
        addGroupInputRef.current.focus();
      }
    }, 100);
  };

  // 새 그룹 추가 제출 핸들러
  const handleAddGroupSubmit = async () => {
    if (newGroupName.trim()) {
      try {
        setLoading(true);
        const response = await createGroup({ groupName: newGroupName.trim() });
        
        if (response && response.success) {
          notification.success({
            message: t('users.groupAdd.doneTitle'),
            description: t('users.groupAdd.doneDesc'),
            placement: 'top',
            duration: 3,
          });
          // 성공 후 상태 초기화 및 그룹 목록 다시 로드
          setNewGroupName('');
          setShowAddGroupInput(false);
          fetchGroupList();
        } else {
          // 중복 여부에 따라 다른 메시지 표시
          if (response && response.isDuplicate) {
            notification.warning({
              message: t('users.groupAdd.dupTitle'),
              description: t('users.groupAdd.dupDesc'),
              placement: 'top',
              duration: 3,
            });
          } else {
            notification.error({
              message: t('users.groupAdd.failTitle'),
              description: apiErrorText(response, 'users.groupAdd.failDesc'),
              placement: 'top',
              duration: 3,
            });
          }
        }
      } catch (error) {
        console.error('그룹 추가 오류:', error);
        notification.error({
          message: t('users.groupAdd.failTitle'),
          description: t('users.groupAdd.failDesc'),
          placement: 'top',
          duration: 3,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // 입력 취소 핸들러
  const handleCancelAddGroup = (e) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    setShowAddGroupInput(false);
    setNewGroupName('');
  };

  // 그룹 삭제 핸들러
  const handleDeleteGroup = async (e, groupId) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    
    Modal.confirm({
      title: t('users.groupDel.title'),
      content: (
        <div style={{
          fontSize: '14px',
          lineHeight: '1.8',
          marginTop: '8px'
        }}>
          {t('users.groupDel.line1')}
          <br />
          {t('users.groupDel.line2')}
        </div>
      ),
      okText: t('users.ok'),
      cancelText: t('users.cancel'),
      centered: true,
      width: 400,
      className: 'custom-modal',
      okButtonProps: {
        style: {
          background: '#1890ff',
          borderColor: '#1890ff'
        }
      },
      cancelButtonProps: {
        style: {
          marginRight: '8px'
        }
      },
      onOk: async () => {
        try {
          setLoading(true);
          const response = await removeGroup(groupId);
          
          if (response && response.success) {
            notification.success({
              message: t('users.groupDel.doneTitle'),
              description: t('users.groupDel.doneDesc'),
              placement: 'top',
              duration: 3,
            });
            
            // 성공 후 그룹 목록 다시 로드
            fetchGroupList();
            
            // 현재 선택된 그룹이 삭제된 그룹이면 필터 초기화
            if (tableParams.groupId === groupId || selectedGroup === groupId) {
              setSelectedGroup(null);
              const newParams = {
                ...tableParams,
                groupId: undefined
              };
              setTableParams(newParams);
              getTableUserList(newParams);
            }

            // 삭제된 그룹에 속한 사용자들의 그룹 정보 업데이트
            setData(prevData => 
              prevData.map(user => 
                user.groupId === groupId 
                  ? { ...user, groupId: null, groupName: null }
                  : user
              )
            );
            
          } else {
            notification.error({
              message: t('users.groupDel.failTitle'),
              description: apiErrorText(response, 'users.groupDel.failDesc'),
              placement: 'top',
              duration: 3,
            });
          }
        } catch (error) {
          console.error('그룹 삭제 오류:', error);
          notification.error({
            message: t('users.groupDel.failTitle'),
            description: t('users.groupDel.failDesc'),
            placement: 'top',
            duration: 3,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 그룹 드롭다운 메뉴 아이템 생성
  const getGroupMenuItems = () => {
    const items = [];

    // ALL 옵션 추가 (맨 위에 배치)
    items.push({
      key: 'all',
      label: (
        <div style={{ 
          fontWeight: selectedGroup === null ? 'bold' : 'normal',
          color: selectedGroup === null ? '#1890ff' : 'inherit'
        }}>
          ALL
        </div>
      ),
    });

    // 그룹 목록 추가
    if (groupList && groupList.length > 0) {
      // 그룹이 있는 경우에만 구분선 추가
      items.push({
        type: 'divider',
      });

      groupList.forEach(group => {
        const isSelected = selectedGroup === group.id;
        items.push({
          key: group.id.toString(),
          label: (
            <div className="group-item-container">
              <span 
                className="group-item-name"
                style={{ 
                  fontWeight: isSelected ? 'bold' : 'normal',
                  color: isSelected ? '#1890ff' : 'inherit'
                }}
              >
                {group.name || group.groupName || t('users.group.fallbackName', { id: group.id })}
              </span>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className="delete-group-button"
                onClick={(e) => handleDeleteGroup(e, group.id)}
                size="small"
              />
            </div>
          ),
        });
      });
      
      // 그룹 추가 전 구분선 추가
      items.push({
        type: 'divider',
      });
    }

    // 그룹 추가 메뉴 아이템 또는 입력 필드
    if (showAddGroupInput) {
      items.push({
        key: 'add-input',
        label: (
          <div onClick={(e) => e.stopPropagation()} className="add-group-input-container">
            <Input
              ref={addGroupInputRef}
              placeholder={t('users.placeholder.groupName')}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onPressEnter={handleAddGroupSubmit}
              onBlur={handleCancelAddGroup}
              size="small"
            />
          </div>
        ),
      });
    } else {
      items.push({
        key: 'add',
        label: (
          <div onClick={handleAddGroup} style={{ display: 'flex', alignItems: 'center' }}>
            <PlusOutlined style={{ marginRight: 8 }} /> add
          </div>
        ),
      });
    }

    return items;
  };

  // 초기 데이터 로딩
  useEffect(() => {
    getTableUserList(tableParams);
    fetchGroupList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, []);

  return (
    <div>
      <div className="page-title p-16">{t('pageTitle.users')}</div>
      
      {/* 그룹 드롭다운 추가 */}
      <div className="group-filter-container">
        <Dropdown
          menu={{
            items: getGroupMenuItems(),
            onClick: handleGroupSelect,
            style: { overflow: 'visible' }
          }}
          trigger={['click']}
          onOpenChange={(visible) => {
            if (!visible) {
              setShowAddGroupInput(false);
              setNewGroupName('');
            }
          }}
          overlayClassName="group-dropdown-overlay"
          dropdownRender={(menu) => (
            <div className="custom-dropdown-menu">
              {menu}
            </div>
          )}
          placement="bottomLeft"
        >
          <div className="group-dropdown-button">
            <Space>
              {selectedGroup === null 
                ? 'Groups (ALL)' 
                : `Groups (${groupList.find(g => g.id === selectedGroup)?.name || groupList.find(g => g.id === selectedGroup)?.groupName || 'Unknown'})`
              }
              <DownOutlined />
            </Space>
          </div>
        </Dropdown>
      </div>
      
      <div className="wrapper-user p-16">
        <TableUser
          data={data}
          tableParams={tableParams}
          onChangePage={onChangePage}
          loading={loading}
          onSelectChange={onSelectChange}
          selectedRowKeys={selectedRowKeys}
          totalElements={totalElements}
          onDeleteItem={showConfirm}
          updatePassword={handleUpdatePassword}
          updateUser={handleUpdateUser}
          groupList={groupList}
          handleGroupChange={handleGroupChange}
          updateUserGroup={handleGroupChange}
        />
        <Modal
          open={loading}
          footer={null}
          closable={false}
          centered
          width={300}
        >
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '10px' }}>{t('users.loading')}</p>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default User; 